// Angular
import { Component, ViewChild, OnInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

// Grid
import { CerFormComponent } from '@form/cer-form.component';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { CerAppFileViewerTabComponent, TabSelectedApplyData } from 'src/cer-app/cer-app-file-viewer-tab/cer-app-file-viewer-tab.component';

// WebApi
import {
  ChatDto,
  LinkedAttachmentDto,
  VoucherMessageViewDetailsVm,
} from 'src/app/api';

// App
import { VoucherDataService } from '../voucher-data.service';
import { VoucherViewService } from './voucher-view.service';
import { VoucherPostingViewService } from './voucherposting-view.service';
import { VoucherAttachmentService } from '../voucher-attachment.service';
import { CerFormPanesSettings } from '@form/cer-form-panes/cer-form-panes.service';
import { UiActionTypeEnum, UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { FieldMetadata, ViewMetadata } from 'src/cer/cer-data/cer-data-types';
import { CustInvoiceService } from 'src/app/sales/cust-invoice.service';
import { CerFormPaneComponent } from '@form/cer-form-panes/cer-form-pane/cer-form-pane.component';
import { ICerFormViewCustomComponent } from '@form/cer-form-view-type/icer-form-view-custom-component';
import { CerFormView } from '@form/cer-form-view';
import { CerFormViewSet } from '@form/cer-form-view-set';

@Component({
  selector: 'voucher-list',
  templateUrl: './voucher-list.component.html',
  styleUrls: ['../voucher.css'],
  providers: [VoucherDataService, VoucherViewService, VoucherPostingViewService, VoucherAttachmentService]
})

export class VoucherListComponent implements OnInit, OnDestroy, ICerFormViewCustomComponent {

  // State
  protected setupComplete: boolean = false;
  protected custInvoiceFilter: boolean = false;

  // File Viewer Tab State
  protected attachments: LinkedAttachmentDto[];
  protected chats: ChatDto[];
  protected attachmentToken: string;
  protected attachmentRefRowId: number;
  protected attachmentSaveUrl: string;
  protected attachmentRemoveUrl: string;

  protected invoiceViewMetadata: ViewMetadata;
  protected invoiceFieldMetadata: FieldMetadata[];
  protected invoiceToolbarCommands: UiCommand[];

  protected invoiceLineViewMetadata: ViewMetadata;
  protected invoiceLineFieldMetadata: FieldMetadata[];
  protected invoiceLineToolbarCommands: UiCommand[];

  constructor(
    activatedRoute: ActivatedRoute,
    private voucherDataService: VoucherDataService,
    private voucherViewService: VoucherViewService,
    private voucherPostingViewService: VoucherPostingViewService,
    private custInvoiceService: CustInvoiceService,
    protected attachmentService: VoucherAttachmentService) {

    this.voucherDataService.init(activatedRoute);
    this.manage(this.voucherViewService.panesSetupComplete$.subscribe(panes => this.setup(panes)));
  }

  public ngOnInit(): void {
    this.voucherViewService.init();
    this.voucherPostingViewService.init();
    this.manage(this.voucherDataService.detailsVm$.subscribe(vm => this.onDetailVm(vm)));
  }

  private subscriptionManager: Subscription = new Subscription();
  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  public ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  public setView(view: CerFormView) {
    view.dataChildrenExists = true;
    this.voucherViewService.componentView = view;
    this.voucherPostingViewService.componentView = view;
  }

  public onViewCommand(e: UiCommandEvent): void {
    this.form?.formService.formCommandNext(e);
  }

  // Form
  private form: CerFormComponent;
  @ViewChild('form', { static: false }) set formContent(content: CerFormComponent) {
    if (content && this.form != content) {
      this.form = content;
      this.voucherViewService.initForm(this.form);
      this.initFileViewerTab();
    }
  }

  // Panes
  private paneComponents: QueryList<CerFormPaneComponent>;
  @ViewChildren(CerFormPaneComponent) set setPaneComponents(paneComponents: QueryList<CerFormPaneComponent>) {
    if (paneComponents?.length > 0 && this.form != null) {
      this.paneComponents = paneComponents;
      this.viewSetPostingAdd();
      this.form.initContext(paneComponents);
    }
  }

  private viewSetPosting : CerFormViewSet;
  private viewSetPostingAdd() {
    if (this.paneComponents?.length > 0 && this.grid) {
      if (this.viewSetPosting == null) {
        this.viewSetPosting = this.voucherPostingViewService.viewSetGet('bottom', this.grid);
        this.form.formContextService.viewSetShow(this.viewSetPosting);
      }
    }
  }

  protected panes: CerFormPanesSettings = null;
  private setup(panes: CerFormPanesSettings): void {
    this.panes = panes;
    if (this.form) {
      this.form.panes = this.panes;
    }
    this.setupComplete = true;
  }


  // FileViewerTab
  private tabAttachments: CerAppFileViewerTabComponent;
  @ViewChild('tabAttachments', { static: false }) set tabContent(content: CerAppFileViewerTabComponent) {
    if (content && this.tabAttachments != content) {
      this.tabAttachments = content;
      this.initFileViewerTab();
    }
  }

  private initFileViewerTab() {
    if (this.tabAttachments) {
      if (this.form) {
        this.attachmentService.init(this.form, this.tabAttachments);
      }
      this.tabAttachments.privateNoteActive = this.voucherDataService.userIsAdmin;
    }
    this.voucherViewService.fileViewerTab = this.tabAttachments;
  }

  // Grid
  private grid: CerGridComponent;
  @ViewChild('grid', { static: false }) set gridContent(content: CerGridComponent) {
    if (content && this.grid != content) {
      this.grid = content;
      this.voucherViewService.initGrid(this.grid);
      this.voucherDataService.initGrid(this.grid);
      this.grid.init();
      this.manage(this.grid.commmand.subscribe(cmd => this.onGridCommand(cmd)));
      this.viewSetPostingAdd();
    }
  }

  protected onGridCommand(cmd: UiCommandEvent) {
    if (cmd.actionType == UiActionTypeEnum.ChatClicked) {
      if (this.tabAttachments) {
        this.attachmentIdxDefault = this.attachments?.length ?? 0;
      }
      this.voucherViewService.chatOpen();
    }
  }

  // CustInvoice grid
  private gridCustInvoice: CerGridComponent;
  @ViewChild('gridCustInvoice', { static: false }) set gridCustInvoiceContent(content: CerGridComponent) {
    if (content && !this.gridCustInvoice) {
      this.gridCustInvoice = content;
      this.manage(this.gridCustInvoice.commmand.subscribe(event => this.custInvoiceService.onCommand(event)));
      //this.voucherDataService.initCustInvoiceGrid(this.gridCustInvoice);
    }
  }

  // File viewer
  protected attachmentIdxDefault: number = null;
  private onDetailVm(vm: VoucherMessageViewDetailsVm) {
    this.chats = vm?.chatDtoList ?? [];
    this.attachmentToken = vm?.voucherMessageViewDto?.messageToken;
    this.attachmentRefRowId = vm?.voucherMessageViewDto?.id;
    this.attachments = vm?.voucherMessageAttachmentDtoList ?? [];
    this.attachmentIdxDefault = this.attachmentService.getAttachmentSelectextIdxDefault(this.attachments) ?? 0;
    this.attachmentSaveUrl = vm?.attachmentsSaveUrl;
    this.attachmentRemoveUrl = vm?.attachmentsRemoveUrl;
    if (this.grid && vm?.voucherViewDtoList) {
      (<any>vm.voucherViewDtoList).chat = this.chats;
      this.grid.editCellService.chatColumnRefresh(vm.voucherViewDtoList);
    }
  }

  protected onAttachmentTabSelectedIndexChanged(index: number) {
    this.voucherViewService.voucherDetailsDialogAttachmentIdxDefault = index;
  }

  protected onAttachmentCheckBoxApply(data: TabSelectedApplyData) {
    var id: number = this.tabAttachments.refRowId;
    if (id) {
      var callback = () => {
        this.voucherDataService.detailsVmMap.delete(id); // force reload
        this.voucherViewService.research();
      }
      this.attachmentService.messageAttachmentsApply(data, callback);
    }
  }
}

